import React, { useState } from 'react';
import { Search, Plus, Mail, Phone, Building2, Map, List } from 'lucide-react';
import { MapContainer, TileLayer, Marker, Popup, Circle } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
// Fix for default marker icons in react-leaflet
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png'
});
// Custom icon for user location
const userLocationIcon = new L.Icon({
  iconUrl: 'data:image/svg+xml;base64,' + btoa(`
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="32" height="32">
      <circle cx="12" cy="12" r="10" fill="#DC2626" stroke="white" stroke-width="2"/>
      <circle cx="12" cy="12" r="4" fill="white"/>
    </svg>
  `),
  iconSize: [32, 32],
  iconAnchor: [16, 16],
  popupAnchor: [0, -16]
});
// Custom icons for different contact categories - Classic pin shape
const clientIcon = new L.Icon({
  iconUrl: 'data:image/svg+xml;base64,' + btoa(`
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 36" width="32" height="48">
      <path d="M12 0C5.4 0 0 5.4 0 12c0 8 12 24 12 24s12-16 12-24c0-6.6-5.4-12-12-12z" fill="#3B82F6" stroke="white" stroke-width="1.5"/>
      <circle cx="12" cy="12" r="5" fill="white"/>
    </svg>
  `),
  iconSize: [32, 48],
  iconAnchor: [16, 48],
  popupAnchor: [0, -48]
});
const subcontractorIcon = new L.Icon({
  iconUrl: 'data:image/svg+xml;base64,' + btoa(`
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 36" width="32" height="48">
      <path d="M12 0C5.4 0 0 5.4 0 12c0 8 12 24 12 24s12-16 12-24c0-6.6-5.4-12-12-12z" fill="#9333EA" stroke="white" stroke-width="1.5"/>
      <circle cx="12" cy="12" r="5" fill="white"/>
    </svg>
  `),
  iconSize: [32, 48],
  iconAnchor: [16, 48],
  popupAnchor: [0, -48]
});
const supplierIcon = new L.Icon({
  iconUrl: 'data:image/svg+xml;base64,' + btoa(`
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 36" width="32" height="48">
      <path d="M12 0C5.4 0 0 5.4 0 12c0 8 12 24 12 24s12-16 12-24c0-6.6-5.4-12-12-12z" fill="#10B981" stroke="white" stroke-width="1.5"/>
      <circle cx="12" cy="12" r="5" fill="white"/>
    </svg>
  `),
  iconSize: [32, 48],
  iconAnchor: [16, 48],
  popupAnchor: [0, -48]
});
const otherIcon = new L.Icon({
  iconUrl: 'data:image/svg+xml;base64,' + btoa(`
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 36" width="32" height="48">
      <path d="M12 0C5.4 0 0 5.4 0 12c0 8 12 24 12 24s12-16 12-24c0-6.6-5.4-12-12-12z" fill="#6B7280" stroke="white" stroke-width="1.5"/>
      <circle cx="12" cy="12" r="5" fill="white"/>
    </svg>
  `),
  iconSize: [32, 48],
  iconAnchor: [16, 48],
  popupAnchor: [0, -48]
});
interface Contact {
  id: number;
  name: string;
  company: string;
  role: string;
  email: string;
  phone: string;
  region: string;
  category: 'client' | 'subcontractor' | 'supplier' | 'other';
  coordinates: [number, number];
}
export function Contacts() {
  const [viewMode, setViewMode] = useState<'list' | 'map'>('list');
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [searchQuery, setSearchQuery] = useState('');
  // Mock user location (Quebec City)
  const userLocation: [number, number] = [46.8139, -71.208];
  const contacts: Contact[] = [
  // Montréal Region
  {
    id: 1,
    name: 'Marie Dubois',
    company: 'MTQ',
    role: 'Chargée de projet',
    email: 'marie.dubois@mtq.gouv.qc.ca',
    phone: '514-555-0123',
    region: 'Montréal',
    category: 'client',
    coordinates: [45.5017, -73.5673]
  }, {
    id: 2,
    name: 'Jean Tremblay',
    company: 'Ville de Laval',
    role: 'Directeur travaux publics',
    email: 'j.tremblay@laval.ca',
    phone: '450-555-0124',
    region: 'Laval',
    category: 'client',
    coordinates: [45.6066, -73.7124]
  }, {
    id: 3,
    name: 'Sophie Gagnon',
    company: 'Construction ABC',
    role: 'Estimatrice',
    email: 'sgagnon@abc.com',
    phone: '514-555-0125',
    region: 'Montréal',
    category: 'subcontractor',
    coordinates: [45.5236, -73.5987]
  }, {
    id: 4,
    name: 'Pierre Roy',
    company: 'Ville de Québec',
    role: 'Ingénieur civil',
    email: 'p.roy@ville.quebec.qc.ca',
    phone: '418-555-0126',
    region: 'Québec',
    category: 'client',
    coordinates: [46.8139, -71.208]
  }, {
    id: 5,
    name: 'Lucie Martin',
    company: 'Matériaux Plus',
    role: 'Représentante',
    email: 'lmartin@materiauxplus.com',
    phone: '514-555-0127',
    region: 'Montréal',
    category: 'supplier',
    coordinates: [45.4876, -73.6321]
  }, {
    id: 6,
    name: 'Marc Lefebvre',
    company: 'Béton St-Laurent',
    role: 'Directeur commercial',
    email: 'mlefebvre@betonstlaurent.com',
    phone: '514-555-0128',
    region: 'Montréal',
    category: 'supplier',
    coordinates: [45.5419, -73.6359]
  }, {
    id: 7,
    name: 'Isabelle Côté',
    company: 'Ville de Longueuil',
    role: 'Ingénieure municipale',
    email: 'i.cote@longueuil.ca',
    phone: '450-555-0129',
    region: 'Longueuil',
    category: 'client',
    coordinates: [45.5312, -73.5185]
  }, {
    id: 8,
    name: 'François Bouchard',
    company: 'Pavage Excellence',
    role: 'Président',
    email: 'fbouchard@pavageexcellence.com',
    phone: '514-555-0130',
    region: 'Montréal',
    category: 'subcontractor',
    coordinates: [45.4642, -73.5497]
  }, {
    id: 9,
    name: 'Nathalie Bergeron',
    company: 'Acier Québec',
    role: 'Représentante',
    email: 'nbergeron@acierquebec.com',
    phone: '418-555-0131',
    region: 'Québec',
    category: 'supplier',
    coordinates: [46.8456, -71.2642]
  }, {
    id: 10,
    name: 'Daniel Gauthier',
    company: 'Ville de Gatineau',
    role: 'Chef de division',
    email: 'd.gauthier@gatineau.ca',
    phone: '819-555-0132',
    region: 'Gatineau',
    category: 'client',
    coordinates: [45.4765, -75.7013]
  },
  // Québec City Region
  {
    id: 11,
    name: 'Caroline Morin',
    company: 'Excavation Capitale',
    role: 'Directrice',
    email: 'cmorin@excavationcapitale.com',
    phone: '418-555-0133',
    region: 'Québec',
    category: 'subcontractor',
    coordinates: [46.8289, -71.2384]
  }, {
    id: 12,
    name: 'Alain Pelletier',
    company: 'Ville de Lévis',
    role: 'Coordonnateur projets',
    email: 'a.pelletier@ville.levis.qc.ca',
    phone: '418-555-0134',
    region: 'Lévis',
    category: 'client',
    coordinates: [46.8041, -71.1778]
  }, {
    id: 13,
    name: 'Sylvie Lavoie',
    company: 'Granulats Québec',
    role: 'Gestionnaire',
    email: 'slavoie@granulatqc.com',
    phone: '418-555-0135',
    region: 'Québec',
    category: 'supplier',
    coordinates: [46.8653, -71.2987]
  }, {
    id: 14,
    name: 'Robert Girard',
    company: 'Construction Girard',
    role: 'Estimateur',
    email: 'rgirard@constructiongirard.com',
    phone: '418-555-0136',
    region: 'Québec',
    category: 'subcontractor',
    coordinates: [46.7985, -71.2456]
  }, {
    id: 15,
    name: 'Julie Fortin',
    company: 'MTQ Capitale-Nationale',
    role: 'Ingénieure',
    email: 'julie.fortin@mtq.gouv.qc.ca',
    phone: '418-555-0137',
    region: 'Québec',
    category: 'client',
    coordinates: [46.8123, -71.2145]
  },
  // Sherbrooke Region
  {
    id: 16,
    name: 'Michel Bélanger',
    company: 'Ville de Sherbrooke',
    role: 'Directeur travaux publics',
    email: 'm.belanger@ville.sherbrooke.qc.ca',
    phone: '819-555-0138',
    region: 'Sherbrooke',
    category: 'client',
    coordinates: [45.4042, -71.8929]
  }, {
    id: 17,
    name: 'Annie Nadeau',
    company: 'Béton Estrie',
    role: 'Représentante',
    email: 'anadeau@betonestrie.com',
    phone: '819-555-0139',
    region: 'Sherbrooke',
    category: 'supplier',
    coordinates: [45.3876, -71.9123]
  }, {
    id: 18,
    name: 'Luc Mercier',
    company: 'Pavage Sherbrooke',
    role: 'Contremaître',
    email: 'lmercier@pavagesherbrooke.com',
    phone: '819-555-0140',
    region: 'Sherbrooke',
    category: 'subcontractor',
    coordinates: [45.4215, -71.8765]
  },
  // Trois-Rivières Region
  {
    id: 19,
    name: 'Chantal Rousseau',
    company: 'Ville de Trois-Rivières',
    role: 'Chargée de projet',
    email: 'c.rousseau@v3r.net',
    phone: '819-555-0141',
    region: 'Trois-Rivières',
    category: 'client',
    coordinates: [46.3432, -72.5477]
  }, {
    id: 20,
    name: 'Stéphane Poirier',
    company: 'Asphalte Mauricie',
    role: 'Directeur',
    email: 'spoirier@asphaltemauricie.com',
    phone: '819-555-0142',
    region: 'Trois-Rivières',
    category: 'subcontractor',
    coordinates: [46.3598, -72.5234]
  }, {
    id: 21,
    name: 'Valérie Caron',
    company: 'Matériaux Mauricie',
    role: 'Représentante',
    email: 'vcaron@materiauxmauricie.com',
    phone: '819-555-0143',
    region: 'Trois-Rivières',
    category: 'supplier',
    coordinates: [46.3287, -72.5789]
  },
  // Saguenay Region
  {
    id: 22,
    name: 'Martin Simard',
    company: 'Ville de Saguenay',
    role: 'Ingénieur civil',
    email: 'm.simard@ville.saguenay.qc.ca',
    phone: '418-555-0144',
    region: 'Saguenay',
    category: 'client',
    coordinates: [48.4284, -71.0656]
  }, {
    id: 23,
    name: 'Josée Tremblay',
    company: 'Construction Saguenay',
    role: 'Estimatrice',
    email: 'jtremblay@constructionsaguenay.com',
    phone: '418-555-0145',
    region: 'Saguenay',
    category: 'subcontractor',
    coordinates: [48.4165, -71.0512]
  }, {
    id: 24,
    name: 'Claude Boucher',
    company: 'Béton du Fjord',
    role: 'Directeur',
    email: 'cboucher@betondufjord.com',
    phone: '418-555-0146',
    region: 'Saguenay',
    category: 'supplier',
    coordinates: [48.4423, -71.0789]
  },
  // Rimouski Region
  {
    id: 25,
    name: 'Louise Rioux',
    company: 'Ville de Rimouski',
    role: 'Coordonnatrice',
    email: 'l.rioux@ville.rimouski.qc.ca',
    phone: '418-555-0147',
    region: 'Rimouski',
    category: 'client',
    coordinates: [48.4489, -68.5236]
  }, {
    id: 26,
    name: 'Éric Levesque',
    company: 'Excavation Bas-St-Laurent',
    role: 'Propriétaire',
    email: 'elevesque@excavationbsl.com',
    phone: '418-555-0148',
    region: 'Rimouski',
    category: 'subcontractor',
    coordinates: [48.4623, -68.5412]
  },
  // Drummondville Region
  {
    id: 27,
    name: 'Diane Hébert',
    company: 'Ville de Drummondville',
    role: 'Ingénieure',
    email: 'd.hebert@drummondville.ca',
    phone: '819-555-0149',
    region: 'Drummondville',
    category: 'client',
    coordinates: [45.8833, -72.4833]
  }, {
    id: 28,
    name: 'Guy Cloutier',
    company: 'Pavage Centre-du-Québec',
    role: 'Contremaître',
    email: 'gcloutier@pavagecdq.com',
    phone: '819-555-0150',
    region: 'Drummondville',
    category: 'subcontractor',
    coordinates: [45.8765, -72.4987]
  },
  // Granby Region
  {
    id: 29,
    name: 'Nicole Gagnon',
    company: 'Ville de Granby',
    role: 'Directrice',
    email: 'n.gagnon@ville.granby.qc.ca',
    phone: '450-555-0151',
    region: 'Granby',
    category: 'client',
    coordinates: [45.4, -72.7333]
  }, {
    id: 30,
    name: 'Patrick Labelle',
    company: 'Construction Montérégie',
    role: 'Estimateur',
    email: 'plabelle@constructionmonteregie.com',
    phone: '450-555-0152',
    region: 'Granby',
    category: 'subcontractor',
    coordinates: [45.4123, -72.7198]
  },
  // Saint-Hyacinthe Region
  {
    id: 31,
    name: 'Sylvain Desrochers',
    company: 'Ville de Saint-Hyacinthe',
    role: 'Chef de projet',
    email: 's.desrochers@vshy.ca',
    phone: '450-555-0153',
    region: 'Saint-Hyacinthe',
    category: 'client',
    coordinates: [45.6308, -72.9578]
  }, {
    id: 32,
    name: 'Manon Dupuis',
    company: 'Béton Maskoutain',
    role: 'Représentante',
    email: 'mdupuis@betonmaskoutain.com',
    phone: '450-555-0154',
    region: 'Saint-Hyacinthe',
    category: 'supplier',
    coordinates: [45.6187, -72.9432]
  },
  // Joliette Region
  {
    id: 33,
    name: 'Richard Dufour',
    company: 'Ville de Joliette',
    role: 'Ingénieur',
    email: 'r.dufour@ville.joliette.qc.ca',
    phone: '450-555-0155',
    region: 'Joliette',
    category: 'client',
    coordinates: [46.0227, -73.4456]
  }, {
    id: 34,
    name: 'Céline Beaupré',
    company: 'Excavation Lanaudière',
    role: 'Directrice',
    email: 'cbeaupre@excavationlanaudiere.com',
    phone: '450-555-0156',
    region: 'Joliette',
    category: 'subcontractor',
    coordinates: [46.0345, -73.4289]
  },
  // Repentigny Region
  {
    id: 35,
    name: 'André Boisvert',
    company: 'Ville de Repentigny',
    role: 'Coordonnateur',
    email: 'a.boisvert@repentigny.ca',
    phone: '450-555-0157',
    region: 'Repentigny',
    category: 'client',
    coordinates: [45.7421, -73.4501]
  }, {
    id: 36,
    name: 'Hélène Paquette',
    company: 'Asphalte Rive-Nord',
    role: 'Estimatrice',
    email: 'hpaquette@asphalterivnord.com',
    phone: '450-555-0158',
    region: 'Repentigny',
    category: 'subcontractor',
    coordinates: [45.7298, -73.4623]
  },
  // Terrebonne Region
  {
    id: 37,
    name: 'Jacques Leblanc',
    company: 'Ville de Terrebonne',
    role: 'Directeur',
    email: 'j.leblanc@ville.terrebonne.qc.ca',
    phone: '450-555-0159',
    region: 'Terrebonne',
    category: 'client',
    coordinates: [45.7001, -73.6471]
  }, {
    id: 38,
    name: 'Monique Lalonde',
    company: 'Matériaux Basses-Laurentides',
    role: 'Représentante',
    email: 'mlalonde@materiauxbl.com',
    phone: '450-555-0160',
    region: 'Terrebonne',
    category: 'supplier',
    coordinates: [45.6876, -73.6598]
  },
  // Blainville Region
  {
    id: 39,
    name: 'Paul Germain',
    company: 'Ville de Blainville',
    role: 'Ingénieur municipal',
    email: 'p.germain@ville.blainville.qc.ca',
    phone: '450-555-0161',
    region: 'Blainville',
    category: 'client',
    coordinates: [45.6697, -73.8807]
  }, {
    id: 40,
    name: 'France Demers',
    company: 'Construction Laurentides',
    role: 'Contremaître',
    email: 'fdemers@constructionlaurentides.com',
    phone: '450-555-0162',
    region: 'Blainville',
    category: 'subcontractor',
    coordinates: [45.6543, -73.8965]
  },
  // Brossard Region
  {
    id: 41,
    name: 'Denis Marchand',
    company: 'Ville de Brossard',
    role: 'Chef de division',
    email: 'd.marchand@ville.brossard.qc.ca',
    phone: '450-555-0163',
    region: 'Brossard',
    category: 'client',
    coordinates: [45.4504, -73.4656]
  }, {
    id: 42,
    name: 'Suzanne Turcotte',
    company: 'Béton Rive-Sud',
    role: 'Directrice commerciale',
    email: 'sturcotte@betonrivesud.com',
    phone: '450-555-0164',
    region: 'Brossard',
    category: 'supplier',
    coordinates: [45.4387, -73.4523]
  },
  // Saint-Jean-sur-Richelieu Region
  {
    id: 43,
    name: 'Yves Beaulieu',
    company: 'Ville de Saint-Jean-sur-Richelieu',
    role: 'Ingénieur',
    email: 'y.beaulieu@sjsr.ca',
    phone: '450-555-0165',
    region: 'Saint-Jean-sur-Richelieu',
    category: 'client',
    coordinates: [45.3075, -73.2654]
  }, {
    id: 44,
    name: 'Johanne Carrier',
    company: 'Pavage Haut-Richelieu',
    role: 'Estimatrice',
    email: 'jcarrier@pavagehautrichelieu.com',
    phone: '450-555-0166',
    region: 'Saint-Jean-sur-Richelieu',
    category: 'subcontractor',
    coordinates: [45.2987, -73.2789]
  },
  // Victoriaville Region
  {
    id: 45,
    name: 'Bernard Thibault',
    company: 'Ville de Victoriaville',
    role: 'Coordonnateur',
    email: 'b.thibault@victoriaville.ca',
    phone: '819-555-0167',
    region: 'Victoriaville',
    category: 'client',
    coordinates: [46.0525, -71.9642]
  }, {
    id: 46,
    name: 'Ginette Provencher',
    company: 'Excavation Bois-Francs',
    role: 'Propriétaire',
    email: 'gprovencher@excavationbf.com',
    phone: '819-555-0168',
    region: 'Victoriaville',
    category: 'subcontractor',
    coordinates: [46.0398, -71.9789]
  },
  // Rouyn-Noranda Region
  {
    id: 47,
    name: 'Réal Gendron',
    company: 'Ville de Rouyn-Noranda',
    role: 'Directeur',
    email: 'r.gendron@rouyn-noranda.ca',
    phone: '819-555-0169',
    region: 'Rouyn-Noranda',
    category: 'client',
    coordinates: [48.2359, -79.0242]
  }, {
    id: 48,
    name: 'Lise Laflamme',
    company: 'Construction Abitibi',
    role: 'Estimatrice',
    email: 'llaflamme@constructionabitibi.com',
    phone: '819-555-0170',
    region: 'Rouyn-Noranda',
    category: 'subcontractor',
    coordinates: [48.2287, -79.0398]
  },
  // Val-d'Or Region
  {
    id: 49,
    name: 'Claude Audet',
    company: "Ville de Val-d'Or",
    role: 'Ingénieur',
    email: 'c.audet@ville.valdor.qc.ca',
    phone: '819-555-0171',
    region: "Val-d'Or",
    category: 'client',
    coordinates: [48.0977, -77.7827]
  }, {
    id: 50,
    name: 'Martine Leclerc',
    company: 'Matériaux Nordiques',
    role: 'Représentante',
    email: 'mleclerc@materiauxnordiques.com',
    phone: '819-555-0172',
    region: "Val-d'Or",
    category: 'supplier',
    coordinates: [48.1123, -77.7654]
  }];
  const filteredContacts = contacts.filter(contact => {
    const matchesSearch = contact.name.toLowerCase().includes(searchQuery.toLowerCase()) || contact.company.toLowerCase().includes(searchQuery.toLowerCase()) || contact.role.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory = selectedCategory === 'all' || contact.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });
  const getCategoryColor = (category: string) => {
    switch (category) {
      case 'client':
        return 'bg-blue-100 text-blue-800';
      case 'subcontractor':
        return 'bg-purple-100 text-purple-800';
      case 'supplier':
        return 'bg-green-100 text-green-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };
  const getCategoryLabel = (category: string) => {
    switch (category) {
      case 'client':
        return 'Client';
      case 'subcontractor':
        return 'Sous-traitant';
      case 'supplier':
        return 'Fournisseur';
      default:
        return 'Autre';
    }
  };
  const getCategoryIcon = (category: string) => {
    switch (category) {
      case 'client':
        return clientIcon;
      case 'subcontractor':
        return subcontractorIcon;
      case 'supplier':
        return supplierIcon;
      default:
        return otherIcon;
    }
  };
  return <div className="space-y-6" data-id="element-222">
      <div className="flex items-center justify-between" data-id="element-223">
        <h1 className="text-3xl font-bold text-gray-900" data-id="element-224">Contacts</h1>
        <div className="flex items-center space-x-3" data-id="element-225">
          <div className="flex bg-white border border-gray-300 rounded-lg overflow-hidden" data-id="element-226">
            <button onClick={() => setViewMode('list')} className={`px-4 py-2 flex items-center space-x-2 transition-colors ${viewMode === 'list' ? 'bg-red-600 text-white' : 'text-gray-700 hover:bg-gray-50'}`} data-id="element-227">
              <List size={18} data-id="element-228" />
              <span className="font-medium" data-id="element-229">Liste</span>
            </button>
            <button onClick={() => setViewMode('map')} className={`px-4 py-2 flex items-center space-x-2 transition-colors ${viewMode === 'map' ? 'bg-red-600 text-white' : 'text-gray-700 hover:bg-gray-50'}`} data-id="element-230">
              <Map size={18} data-id="element-231" />
              <span className="font-medium" data-id="element-232">Carte</span>
            </button>
          </div>
          <button className="bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 transition-colors font-medium flex items-center space-x-2" data-id="element-233">
            <Plus size={20} data-id="element-234" />
            <span data-id="element-235">Nouveau contact</span>
          </button>
        </div>
      </div>
      {/* Search and Filters */}
      <div className="bg-white rounded-lg border border-gray-200 p-4" data-id="element-236">
        <div className="flex items-center space-x-4 mb-4" data-id="element-237">
          <div className="flex-1 relative" data-id="element-238">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} data-id="element-239" />
            <input type="text" placeholder="Rechercher un contact..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500" data-id="element-240" />
          </div>
        </div>
        {/* Category Filters */}
        <div className="flex space-x-2 overflow-x-auto" data-id="element-241">
          <button onClick={() => setSelectedCategory('all')} className={`px-4 py-2 rounded-lg whitespace-nowrap font-medium transition-colors ${selectedCategory === 'all' ? 'bg-red-600 text-white' : 'bg-red-100 text-red-700 hover:bg-red-200'}`} data-id="element-242">
            Tous
          </button>
          <button onClick={() => setSelectedCategory('client')} className={`px-4 py-2 rounded-lg whitespace-nowrap font-medium transition-colors ${selectedCategory === 'client' ? 'bg-blue-600 text-white' : 'bg-blue-100 text-blue-700 hover:bg-blue-200'}`} data-id="element-243">
            Clients
          </button>
          <button onClick={() => setSelectedCategory('subcontractor')} className={`px-4 py-2 rounded-lg whitespace-nowrap font-medium transition-colors ${selectedCategory === 'subcontractor' ? 'bg-purple-600 text-white' : 'bg-purple-100 text-purple-700 hover:bg-purple-200'}`} data-id="element-244">
            Sous-traitants
          </button>
          <button onClick={() => setSelectedCategory('supplier')} className={`px-4 py-2 rounded-lg whitespace-nowrap font-medium transition-colors ${selectedCategory === 'supplier' ? 'bg-green-600 text-white' : 'bg-green-100 text-green-700 hover:bg-green-200'}`} data-id="element-245">
            Fournisseurs
          </button>
        </div>
      </div>
      {/* Content - List or Map View */}
      {viewMode === 'list' ? <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-id="element-246">
          {filteredContacts.map(contact => <div key={contact.id} className="bg-white rounded-lg border border-gray-200 p-6 hover:shadow-md transition-shadow" data-id="element-247">
              <div className="flex items-start justify-between mb-4" data-id="element-248">
                <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center" data-id="element-249">
                  <span className="text-red-600 font-semibold text-lg" data-id="element-250">
                    {contact.name.split(' ').map(n => n[0]).join('')}
                  </span>
                </div>
              </div>
              <h3 className="font-semibold text-gray-900 mb-1" data-id="element-251">
                {contact.name}
              </h3>
              <p className="text-sm text-gray-600 mb-3" data-id="element-252">{contact.role}</p>
              <div className="space-y-2 mb-4" data-id="element-253">
                <div className="flex items-center text-sm text-gray-600" data-id="element-254">
                  <Building2 size={16} className="mr-2 text-gray-400" data-id="element-255" />
                  <span data-id="element-256">{contact.company}</span>
                </div>
                <div className="flex items-center text-sm text-gray-600" data-id="element-257">
                  <Mail size={16} className="mr-2 text-gray-400" data-id="element-258" />
                  <span className="truncate" data-id="element-259">{contact.email}</span>
                </div>
                <div className="flex items-center text-sm text-gray-600" data-id="element-260">
                  <Phone size={16} className="mr-2 text-gray-400" data-id="element-261" />
                  <span data-id="element-262">{contact.phone}</span>
                </div>
              </div>
              <div className="pt-4 border-t border-gray-200 flex items-center justify-between" data-id="element-263">
                <span className="inline-block px-2 py-1 text-xs font-medium bg-gray-100 text-gray-700 rounded" data-id="element-264">
                  {contact.region}
                </span>
                <span className={`px-2 py-1 text-xs font-medium rounded ${getCategoryColor(contact.category)}`} data-id="element-265">
                  {getCategoryLabel(contact.category)}
                </span>
              </div>
            </div>)}
        </div> : <div className="bg-white rounded-lg border border-gray-200 overflow-hidden" style={{
      height: '600px'
    }} data-id="element-266">
          <MapContainer center={userLocation} zoom={9} style={{
        height: '100%',
        width: '100%'
      }} data-id="element-267">
            <TileLayer attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors' url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" data-id="element-268" />
            {/* User Location Marker */}
            <Marker position={userLocation} icon={userLocationIcon} data-id="element-269">
              <Popup data-id="element-270">
                <div className="p-2" data-id="element-271">
                  <h3 className="font-semibold text-gray-900 mb-1" data-id="element-272">
                    Votre position
                  </h3>
                  <p className="text-sm text-gray-600" data-id="element-273">Vous êtes ici</p>
                </div>
              </Popup>
            </Marker>
            {/* Contact Markers */}
            {filteredContacts.map(contact => <Marker key={contact.id} position={contact.coordinates} icon={getCategoryIcon(contact.category)} data-id="element-274">
                <Popup data-id="element-275">
                  <div className="p-2" data-id="element-276">
                    <h3 className="font-semibold text-gray-900 mb-1" data-id="element-277">
                      {contact.name}
                    </h3>
                    <p className="text-sm text-gray-600 mb-2" data-id="element-278">
                      {contact.company}
                    </p>
                    <p className="text-sm text-gray-600 mb-2" data-id="element-279">{contact.role}</p>
                    <div className="space-y-1" data-id="element-280">
                      <div className="flex items-center text-sm text-gray-600" data-id="element-281">
                        <Phone size={14} className="mr-1" data-id="element-282" />
                        <span data-id="element-283">{contact.phone}</span>
                      </div>
                      <div className="flex items-center text-sm text-gray-600" data-id="element-284">
                        <Mail size={14} className="mr-1" data-id="element-285" />
                        <span className="text-xs" data-id="element-286">{contact.email}</span>
                      </div>
                    </div>
                    <span className={`inline-block mt-2 px-2 py-1 text-xs font-medium rounded ${getCategoryColor(contact.category)}`} data-id="element-287">
                      {getCategoryLabel(contact.category)}
                    </span>
                  </div>
                </Popup>
              </Marker>)}
          </MapContainer>
        </div>}
    </div>;
}